import { getNotificationContent } from '@/api/modules/notification'

const CONTENT_CACHE_LIMIT = 10

/** 管理正文缓存与请求去重；scope 返回当前身份，close/reset 后旧正文不能进入缓存。 */
export function useNotificationContent(scope: () => string) {
  const cache = new Map<number, string>()
  const pending = new Map<number, Promise<string>>()
  let generation = 0

  /** 身份切换或抽屉关闭时同时清缓存、在途索引并使旧请求失效。 */
  function reset() {
    generation++
    cache.clear()
    pending.clear()
  }

  /** 将 id 对应 content 放到缓存末尾，按最近使用顺序淘汰超限内容。 */
  function remember(id: number, content: string) {
    cache.delete(id)
    cache.set(id, content)
    if (cache.size <= CONTENT_CACHE_LIMIT) return
    const oldest = cache.keys().next().value
    if (oldest !== undefined) cache.delete(oldest)
  }

  /** 获取 id 的完整正文；不将摘要写入正文缓存，也不缓存失败。 */
  async function fetchNotificationContent(id: number): Promise<string> {
    if (id <= 0 || !scope()) throw new Error('无效通知或身份')
    if (cache.has(id)) {
      const content = cache.get(id) ?? ''
      remember(id, content)
      return content
    }
    const existing = pending.get(id)
    if (existing) return existing
    const currentScope = scope()
    const version = generation
    const request = getNotificationContent(id)
      .then(({ content }) => {
        if (version !== generation || currentScope !== scope()) throw new Error('通知上下文已失效')
        const text = content ?? ''
        remember(id, text)
        return text
      })
      .finally(() => {
        if (pending.get(id) === request) pending.delete(id)
      })
    pending.set(id, request)
    return request
  }

  return { reset, fetchNotificationContent }
}
